<template lang="pug">

div
    p.ui.info.message(v-if="supplierModels.length === 0")
        | Für alle Lieferanten wurde bereits ein Job erstellt

    div(v-else)
        .ui.secondary.menu
            .right.menu
                .item.fitted
                    .supplier-search
                        .ui.labeled.icon.input
                            .ui.label Lieferantensuche:
                            input(
                                type="text"
                                v-model="searchTerm"
                                placeholder="Suchbegriff..."
                                @focus="isFocused = true"
                                @blur="isFocused = false"
                                @input="onInput"
                                @keydown.down.prevent="moveSuggestion(1)"
                                @keydown.up.prevent="moveSuggestion(-1)"
                                @keydown.enter.prevent="applyActiveSuggestion"
                                @keydown.esc="isFocused = false"
                            )
                            i.times.link.icon(
                                v-if="searchTerm"
                                @click="searchTerm = ''"
                                title="Suche zurücksetzen"
                            )
                            i.search.icon(
                                v-else
                            )
                        .keyword-suggestions(v-if="showSuggestions")
                            .suggestion(
                                v-for="(suggestion, index) in keywordSuggestions"
                                v-bind:key="suggestion"
                                v-bind:class="{ active: index === activeSuggestionIndex }"
                                v-on:mousedown.prevent="applySuggestion(suggestion)"
                            ) {{ suggestion }}

        p.ui.info.message(v-if="filteredSupplierModels.length === 0")
            | Keine Lieferanten für den Suchbegriff gefunden

        table.ui.very.basic.fixed.table(v-else)
            thead
                tr
                    th.six.wide Name
                    th.four.wide PLZ, Ort
                    th.one.wide &nbsp;

            tbody
                tr(
                    is="row"
                    v-for="supplierModel in filteredSupplierModels"
                    v-bind:key="supplierModel.id"
                    v-bind:address-model="findAddressModelBySupplierModel(supplierModel)"
                    v-bind:supplier-model="supplierModel"
                    v-on:input="onSupplierAdd"
                )

</template>

<script>
    import * as R from 'ramda'
    import Row from './suppliers-table/Row.vue'

    export default {

        components: { Row },

        computed: {

            filteredSupplierModels() {
                const searchTerms = this.searchTerm.trim().toLowerCase().split(/\s+/).filter(Boolean)

                if (searchTerms.length === 0) {
                    return this.supplierModels
                }

                return this.supplierModels.filter(supplierModel => {
                    const addressModel = this.findAddressModelBySupplierModel(supplierModel)
                    const keywords = (supplierModel.keywords || []).join(' ')
                    const searchableText = [
                        supplierModel.name,
                        addressModel && addressModel.zip,
                        addressModel && addressModel.city,
                        keywords
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase()

                    return searchTerms.every(searchTerm => searchableText.includes(searchTerm))
                })
            },

            activeSearchToken() {
                const value = this.searchTerm.toLowerCase()

                if (/\s$/.test(value)) {
                    return ''
                }

                const tokens = value.trim().split(/\s+/).filter(Boolean)

                return tokens.length > 0 ? tokens[tokens.length - 1] : ''
            },

            keywordSuggestions() {
                const token = this.activeSearchToken

                if (token.length === 0) {
                    return []
                }

                const keywords = new Set()

                this.supplierModels.forEach(supplierModel => {
                    (supplierModel.keywords || []).forEach(keyword => {
                        const normalizedKeyword = keyword.toLowerCase()

                        // präfixbasiert (startsWith) oder teilstring (includes)
                        if (normalizedKeyword.includes(token)) {
                            keywords.add(normalizedKeyword)
                        }
                    })
                })

                return Array.from(keywords)
                    .sort()
                    .slice(0, 10)
                    .map(keyword => this.capitalizeFirstLetter(keyword))
            },

            showSuggestions() {
                return this.isFocused && !this.suppressSuggestions && this.keywordSuggestions.length > 0
            }

        },

        data() {
            return {
                searchTerm: '',
                isFocused: false,
                activeSuggestionIndex: -1,
                suppressSuggestions: false
            }
        },

        watch: {

            searchTerm() {
                this.activeSuggestionIndex = -1
            }

        },

        methods: {

            onSupplierAdd(value) {
                this.$emit('input', value)
            },

            findAddressModelBySupplierModel(supplierModel) {
                return R.find(R.propEq('id', supplierModel.defaultBillingAddress), this.addressModels)
            },

            onInput() {
                this.suppressSuggestions = false
            },

            moveSuggestion(delta) {
                const maxIndex = this.keywordSuggestions.length - 1

                if (maxIndex < 0) {
                    return
                }

                const nextIndex = this.activeSuggestionIndex + delta

                this.activeSuggestionIndex = Math.max(-1, Math.min(nextIndex, maxIndex))
            },

            applyActiveSuggestion() {
                if (this.activeSuggestionIndex >= 0) {
                    this.applySuggestion(this.keywordSuggestions[this.activeSuggestionIndex])
                }
            },

            applySuggestion(suggestion) {
                const tokens = this.searchTerm.trim().split(/\s+/).filter(Boolean)

                if (tokens.length === 0) {
                    this.searchTerm = suggestion
                } else {
                    const prefix = tokens.slice(0, -1).join(' ')

                    this.searchTerm = prefix.length > 0 ? prefix + ' ' + suggestion : suggestion
                }

                this.activeSuggestionIndex = -1
                this.suppressSuggestions = true
            },

            capitalizeFirstLetter(value) {
                return value.charAt(0).toUpperCase() + value.slice(1)
            }

        },

        props: {

            addressModels: {
                type: Array,
                required: true
            },

            supplierModels: {
                type: Array,
                required: true
            },

        }

    }

</script>

<style scoped>

    .supplier-search {
        position: relative;
    }

    .keyword-suggestions {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 100;
        margin-top: 0.25rem;
        background: #fff;
        border: 1px solid rgba(34, 36, 38, 0.15);
        border-radius: 0.28571429rem;
        box-shadow: 0 2px 4px 0 rgba(34, 36, 38, 0.15);
        max-height: 15rem;
        overflow-y: auto;
    }

    .keyword-suggestions .suggestion {
        padding: 0.5rem 0.75rem;
        cursor: pointer;
    }

    .keyword-suggestions .suggestion:hover,
    .keyword-suggestions .suggestion.active {
        background: rgba(0, 0, 0, 0.05);
    }

</style>
